import { PermissionSyncStatus } from 'src/generated/graphql';

/**
 * The permission-sync loader rules, as a pure function.
 *
 * This lives apart from {@link ./AppBootstrap} because it is the part worth reading
 * and testing in isolation: the component only renders what this returns.
 *
 * Background: a user's permissions live in a DynamoDB cache filled by a background
 * sync. A user whose cache is empty, or whose last sync is older than the server's
 * staleness window, has nothing the app should be rendered from — every page below
 * the bootstrap filters on those permissions, so rendering would show either a UI
 * that looks broken (no cache at all) or one quietly built on a grant set that may
 * have moved since (a stale cache). So the bootstrap waits. The rules decide whether
 * waiting is needed, and when to stop.
 */

/**
 * How long a wait may run before the bootstrap stops treating it as ordinary.
 *
 * ONE constant, both call sites in {@link decideBootstrapAction}, because both answer
 * the same question — has this taken longer than a healthy sync should? — and there is
 * no reason for the two answers to drift apart. What happens at the deadline differs,
 * and that difference is the point:
 *
 * - An IN_PROGRESS run older than this is reported as running long
 *   (`WAIT_EXTENDED`) and keeps being polled. Exceeding the budget is NOT an error
 *   there: the caller cannot tell a slow run from a dead one and the backend can, so
 *   it only changes what the user is told. Measured against the SERVER's
 *   `syncStartedAt`, so a page refresh cannot restart it.
 * - A wait with no run to measure — no status written yet, or a COMPLETED run whose
 *   cache is no longer fresh — gives up at this point instead. That one IS terminal,
 *   and honest: nothing holds ORCA's singleton key, so the refresh the error page
 *   advises fires a genuinely new trigger. Measured from first observation, the only
 *   clock available.
 *
 * A minute rather than something longer because the branch that gives up leaves a
 * user watching a spinner with no way to help themselves, while the branch where
 * guessing "too long" wrong would be expensive — a live run — does not give up at
 * all.
 */
export const PERMISSION_SYNC_WAIT_BUDGET_MS = 60 * 1000;

/** Poll cadence while the loader is up. */
export const PERMISSION_SYNC_POLL_INTERVAL_MS = 2000;

/**
 * Grace window for a FAILED status.
 *
 * A re-trigger fires during `getUserProfile`, but the workflow writes IN_PROGRESS
 * asynchronously, so the first status read can legitimately return the PREVIOUS
 * run's FAILED. Several poll intervals wide, so a stale FAILED gets more than one
 * chance to be superseded rather than racing a single poll; still short enough that a
 * genuinely failed sync surfaces in seconds rather than eating the wait budget.
 */
export const PERMISSION_SYNC_FAILED_GRACE_MS = 10_000;

/** The status payload the rules read. Mirrors `GetPermissionSyncStatus`. */
export interface PermissionSyncSnapshot {
  hasFreshCachedPermissions: boolean;
  syncStatus?: PermissionSyncStatus | null;
  syncStartedAt?: number | null;
}

/**
 * Client-side observation times, for the two states with no server timestamp to
 * measure against. Both are "when this bootstrap first saw that state", and both are
 * legitimately reset by a page refresh — see {@link decideBootstrapAction}.
 */
export interface ClientWaitClocks {
  /** When a wait with no server start time was first observed, if ever. */
  waitWithoutServerStartSince: number | null;
  /** When a FAILED status was first observed, if ever. */
  failedFirstSeenAt: number | null;
}

/**
 * What the bootstrap should do, and why.
 *
 * `WAIT` carries `reevaluateAt`: the epoch millisecond at which this same reading
 * stops meaning "wait". The caller schedules one timer for it, so a wait ends at its
 * deadline even if no poll happens to land at that moment — the decision must not
 * silently inherit the poll cadence as its resolution.
 *
 * `WAIT_EXTENDED` has no deadline. It means the run has outlived the budget but is
 * still in progress, so there is nothing left to time out against: only a new
 * snapshot can move it. Kept distinct from `WAIT` because it is the one state whose
 * copy tells the user it is running long — and, having no automatic exit and no
 * self-service recovery, the only one whose copy points at a human. Distinct from
 * `GIVE_UP` because refreshing cannot help: the trigger is discarded while the run
 * holds ORCA's singleton key, so a refresh returns this same reading.
 */
export type BootstrapAction =
  | { action: 'RENDER'; reason: 'USABLE_CACHE' }
  | {
      action: 'WAIT';
      reason: 'RUN_IN_PROGRESS' | 'AWAITING_STATUS' | 'SYNC_FAILED';
      reevaluateAt: number;
    }
  | { action: 'WAIT_EXTENDED'; reason: 'RUN_OVER_BUDGET' }
  | { action: 'GIVE_UP'; reason: 'AWAITING_STATUS' | 'SYNC_FAILED' };

/**
 * The profile fields {@link hasRenderableCachedPermissions} reads. A structural type
 * rather than the generated `GetUserProfileQuery` shape so the rules stay independent
 * of codegen, matching {@link PermissionSyncSnapshot}.
 */
export interface ProfileCacheSnapshot {
  hasFreshCachedPermissions: boolean;
}

/**
 * Whether the profile already answers the only question the status poll would ask.
 *
 * `getUserProfile` and `getPermissionSyncStatus` read the SAME user row, and
 * `hasFreshCachedPermissions` is the same server-computed value on both. So when the
 * profile reports a fresh cache there is nothing a status read could add: the
 * decision would be row 1 (`RENDER`) whatever the sync status says, since a running or
 * failed BACKGROUND refresh is invisible by design. The bootstrap can skip the second
 * round trip and its loader entirely.
 *
 * A straight boolean read, deliberately: no arithmetic, no clock, no threshold. The
 * freshness window lives server-side only — recomputing it here from `lastSyncedAt`
 * is exactly what the schema forbids, and the window is short enough (hours, not
 * weeks) that a client copy of it would be a live source of disagreement rather than
 * a harmless duplicate.
 *
 * False for a missing or absent profile, which keeps the gate fail-closed: an
 * unanswered question means poll, never render.
 */
export function hasRenderableCachedPermissions(
  profile: ProfileCacheSnapshot | null | undefined,
): boolean {
  return profile?.hasFreshCachedPermissions === true;
}

/**
 * Applies the loader rules to one status reading.
 *
 * WHY AN IN-PROGRESS RUN IS MEASURED AGAINST THE SERVER'S `syncStartedAt`, NOT A
 * CLIENT TIMER: a client timer starts when the page loads, so it restarts on every
 * refresh and could never tell a long-running sync from a fresh one. `syncStartedAt`
 * is the time the RUN began, so `now - syncStartedAt` measures the run's total age
 * however many times the page has been reloaded.
 *
 * The two client-anchored states do not have that hazard: in both, a refresh re-runs
 * `getUserProfile`, which re-fires the trigger, and neither has a run holding ORCA's
 * singleton key to discard it. A refresh there starts a genuinely new attempt, so
 * restarting its clock is correct rather than a loophole.
 *
 * @param snapshot latest status reading
 * @param now epoch millis (injected so the rules stay pure and testable)
 * @param clocks first-observation times for the client-anchored states
 */
export function decideBootstrapAction(
  snapshot: PermissionSyncSnapshot,
  now: number,
  clocks: ClientWaitClocks,
): BootstrapAction {
  // Fresh cache, whatever the status. A running refresh is background work and a
  // failed background refresh is invisible: the next bootstrap retries it. Checked
  // first precisely so IN_PROGRESS and FAILED never reach the cases below when there
  // is something safe to show.
  if (snapshot.hasFreshCachedPermissions) {
    return { action: 'RENDER', reason: 'USABLE_CACHE' };
  }

  const { syncStatus, syncStartedAt } = snapshot;

  // The only state with server truth to measure: a run in progress, which always
  // carries its start time because one DynamoDB write sets both.
  if (
    syncStatus === PermissionSyncStatus.InProgress &&
    typeof syncStartedAt === 'number'
  ) {
    return now - syncStartedAt > PERMISSION_SYNC_WAIT_BUDGET_MS
      ? // Over budget, but the run is alive as far as anyone here can tell. Keep
        // waiting and say so. Erroring would be worse than useless: ORCA discards a
        // re-trigger while this run holds the singleton key, so the refresh the
        // error page advises would return this very reading, forever.
        { action: 'WAIT_EXTENDED', reason: 'RUN_OVER_BUDGET' }
      : {
          action: 'WAIT',
          reason: 'RUN_IN_PROGRESS',
          reevaluateAt: syncStartedAt + PERMISSION_SYNC_WAIT_BUDGET_MS,
        };
  }

  // FAILED. Keep polling briefly: a re-trigger fired during `getUserProfile` and its
  // IN_PROGRESS write may still be landing, in which case the next poll flips us to
  // the in-progress case. If FAILED persists past the grace window it is the real
  // answer, and giving up is honest — the errored execution makes the next trigger
  // eligible for ORCA's rerun recovery, so refreshing does retry.
  if (syncStatus === PermissionSyncStatus.Failed) {
    const failedSince = clocks.failedFirstSeenAt ?? now;
    return now - failedSince > PERMISSION_SYNC_FAILED_GRACE_MS
      ? { action: 'GIVE_UP', reason: 'SYNC_FAILED' }
      : {
          action: 'WAIT',
          reason: 'SYNC_FAILED',
          reevaluateAt: failedSince + PERMISSION_SYNC_FAILED_GRACE_MS,
        };
  }

  // Status absent, or a COMPLETED run whose cache is no longer fresh. Both are the
  // same situation: the trigger fired during `getUserProfile`, the workflow's status
  // write is asynchronous, and it has not landed yet. Wait for it.
  //
  // THE STALE-COMPLETED HALF IS THE ORDINARY CASE, NOT AN EDGE CASE. The freshness
  // window is hours while the cache itself lives for weeks, so any returning user who
  // has been away longer than the window arrives here: a COMPLETED last run, a cache
  // that is present and unexpired, and `hasFreshCachedPermissions` false. That is the
  // designed entry path into this loader — read it as "waiting for the refresh this
  // page load just triggered", not as "something went wrong with the last sync".
  //
  // Bounded from first observation — the only clock available, since there is no run
  // to ask. Giving up here is honest for the same reason as FAILED: no run holds the
  // singleton key, so a refresh re-fires the trigger for real.
  const waitingSince = clocks.waitWithoutServerStartSince ?? now;
  return now - waitingSince > PERMISSION_SYNC_WAIT_BUDGET_MS
    ? { action: 'GIVE_UP', reason: 'AWAITING_STATUS' }
    : {
        action: 'WAIT',
        reason: 'AWAITING_STATUS',
        reevaluateAt: waitingSince + PERMISSION_SYNC_WAIT_BUDGET_MS,
      };
}
